/**
 * ONE PRICE EVERYWHERE for a whole-device replacement (2026-10-29).
 *
 * A replacement's stored price is a leftover: every quote rebuilds it as
 * labor + the new assembly + the install parts. So wherever the pricebook shows
 * "what we charge", a replacement shows the price a quote would actually give
 * — the engine's own sample, worked out on the server — never the stored one.
 */
import { cn } from "@/lib/utils";

export type QuoteTimePrice = {
    /** The size the samples are for — the item's own. */
    size: string;
    /** A flat-rate business customer, before the city's fees and permit. */
    business: number;
    /** A flat-rate home customer; null where the home price is the same rule as business. */
    home: number | null;
};

/** A row the server marked as priced at quote time (the key is present, even when null). */
export function isQuoteTimePriced(it: { quote_time_price?: QuoteTimePrice | null }): boolean {
    return it.quote_time_price !== undefined;
}

const usd = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD" });

/** Plain words for one line: `1" business $1,837.39 · home $1,590.00`. Pure. */
export function quoteTimePriceWords(p: QuoteTimePrice | null | undefined): string {
    if (!p) return "Pick a customer to see it";
    const home = p.home != null ? ` · home ${usd(p.home)}` : "";
    return `${p.size} business ${usd(p.business)}${home}`;
}

export function QuoteTimePriceTag({
    price,
    align = "end",
    className,
}: {
    price: QuoteTimePrice | null | undefined;
    align?: "start" | "end";
    className?: string;
}) {
    return (
        <span
            className={cn("flex flex-col", align === "end" ? "items-end text-right" : "items-start", className)}
            data-testid="quote-time-price"
            title="A replacement is priced on each quote: labor + the new assembly + install parts. The city's permit and fees are added on the quote."
        >
            <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Priced at quote time
            </span>
            {price ? (
                <>
                    <span className="font-semibold tabular-nums">
                        {usd(price.business)}{" "}
                        <span className="text-xs font-normal text-muted-foreground">{price.size} business</span>
                    </span>
                    {price.home != null && (
                        <span className="text-sm tabular-nums">
                            {usd(price.home)} <span className="text-xs text-muted-foreground">{price.size} home</span>
                        </span>
                    )}
                </>
            ) : (
                <span className="text-xs text-muted-foreground">Pick a customer to see it</span>
            )}
        </span>
    );
}
